import { FormEvent, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { deleteFaqResponse, updateFaqResponse } from '../../../api/faq'
import { ReactComponent as PlusCircle } from '../../../assets/icons/add-circle.svg'
import { ReactComponent as CheckmarkCircle } from '../../../assets/icons/checkmark-circle.svg'
import { ReactComponent as Close } from '../../../assets/icons/close.svg'
import { ReactComponent as Earth } from '../../../assets/icons/earth.svg'
import { ReactComponent as Pencil } from '../../../assets/icons/pencil.svg'
import { ReactComponent as Thrash } from '../../../assets/icons/trash.svg'
import { IFaqResponsesItem } from '../faqModels'

import NewResponseForm from './FaqNewResponse/FaqNewResponse'
import FaqResponseYaml from './FaqResponseYaml/FaqResponseYaml'

import styles from './faq-response.module.scss'

interface IFaqResponsesSectionProps {
    data: IFaqResponsesItem[]
    selectedFaqItem: string
    getDataFromApi: () => void
    canDelete: boolean
}

interface IFormData {
    text: string
    value: string
}

function FaqResponsesSection(props: IFaqResponsesSectionProps) {
    const { data, selectedFaqItem, getDataFromApi, canDelete } = props

    const { t } = useTranslation()

    const [addingResponse, setAddingResponse] = useState(false)
    const [editingResponseId, setEditingResponseId] = useState<string | null>(
        null
    )
    const [editFormData, setEditFormData] = useState<IFormData>({
        text: '',
        value: '',
    })

    const handleAddResponse = () => {
        setAddingResponse(true)
    }

    const handleCancelAddResponse = () => {
        setAddingResponse(false)
    }

    const handleDeleteResponse = async (exampleId: string) => {
        if (!canDelete) {
            return
        }

        const confirmDelete = confirm(t('delete_response_confirm'))
        if (!confirmDelete) return

        try {
            await deleteFaqResponse(selectedFaqItem, exampleId)
            getDataFromApi()
        } catch (error) {
            console.error('ERROR:', error)
        }
    }

    const handleEditResponse = (
        responseId: string,
        text: string,
        value: string
    ) => {
        setEditingResponseId(responseId)
        setEditFormData({ text, value })
    }

    const handleFormSubmit = (e: FormEvent, item: any) => {
        e.preventDefault()
        if (editingResponseId) {
            handleSaveEditedResponse(item)
        }
    }

    const handleSaveEditedResponse = async (editedItem: IFaqResponsesItem) => {
        const confirmUpdate = confirm(t('save_response_confirm'))
        if (!confirmUpdate) return

        try {
            const { _id, condition } = editedItem

            await updateFaqResponse(selectedFaqItem, _id, {
                text: editFormData.text,
                condition: [{ ...condition[0], value: editFormData.value }],
            })

            getDataFromApi()

            setEditingResponseId(null)
            setEditFormData({ text: '', value: '' })
        } catch (error) {
            console.error('ERROR:', error)
        }
    }

    return (
        <div className={styles.faqResponse}>
            <div className={styles.faqResponse__header}>
                <h2>{t('responses')}</h2>
                <div>
                    {!addingResponse ? (
                        <button
                            className={styles.faqResponse__btn}
                            onClick={() => handleAddResponse()}
                        >
                            <PlusCircle />
                            {t('add_new')}
                        </button>
                    ) : null}
                </div>
            </div>

            {addingResponse ? (
                <NewResponseForm
                    selectedFaqItem={selectedFaqItem}
                    getDataFromApi={getDataFromApi}
                    onCancel={handleCancelAddResponse}
                />
            ) : null}

            <div className={styles.faqResponse__body}>
                {data &&
                    data.map((item, index) => (
                        <div key={index}>
                            {editingResponseId === item._id ? (
                                <FaqResponseEditForm
                                    onSubmit={(e) => handleFormSubmit(e, item)}
                                    editFormData={editFormData}
                                    setEditFormData={setEditFormData}
                                    onCancel={() => setEditingResponseId(null)}
                                />
                            ) : (
                                <FaqResponseDisplay
                                    item={item}
                                    onEdit={() =>
                                        handleEditResponse(
                                            item._id,
                                            item.text,
                                            item.condition[0].value
                                        )
                                    }
                                    onDelete={() =>
                                        handleDeleteResponse(item._id)
                                    }
                                    selectedFaqItem={selectedFaqItem}
                                    getDataFromApi={getDataFromApi}
                                    canDelete={canDelete}
                                />
                            )}
                        </div>
                    ))}
            </div>
        </div>
    )
}

interface IFaqResponseEditFormProps {
    onSubmit: (e: FormEvent) => void
    editFormData: IFormData
    setEditFormData: (data: IFormData) => void
    onCancel: () => void
}

const FaqResponseEditForm = (props: IFaqResponseEditFormProps) => {
    const { onSubmit, editFormData, setEditFormData, onCancel } = props

    const canSubmit =
        editFormData.text.trim() !== '' && editFormData.value.length === 2

    return (
        <div>
            <form onSubmit={onSubmit}>
                <div className={styles.faqResponse__row}>
                    <div className={styles.faqResponse__textAreaWrapper}>
                        <textarea
                            className={styles.faqResponse__message__textarea}
                            value={editFormData.text}
                            onChange={(e) =>
                                setEditFormData({
                                    ...editFormData,
                                    text: e.target.value,
                                })
                            }
                        />
                    </div>
                    <div className={styles.faqResponse__language}>
                        <Earth
                            className={
                                styles.faqResponse__language__languageIcon
                            }
                        />
                        <input
                            className={
                                styles.faqResponse__language__languageInput
                            }
                            value={editFormData.value}
                            onChange={(e) =>
                                setEditFormData({
                                    ...editFormData,
                                    value: e.target.value,
                                })
                            }
                            maxLength={2}
                        />
                    </div>
                    <button
                        className={`${styles.faqResponse__borderBtn} ${
                            canSubmit ? '' : styles.faqResponse__disabledBtn
                        }`}
                        type="submit"
                        disabled={!canSubmit}
                    >
                        <CheckmarkCircle />
                    </button>
                    <button
                        type="button"
                        className={styles.faqResponse__borderBtn}
                        onClick={onCancel}
                    >
                        <Close />
                    </button>
                </div>
            </form>
        </div>
    )
}

interface IFaqResponseDisplayProps {
    item: IFaqResponsesItem
    onEdit: () => void
    onDelete: () => void
    selectedFaqItem: string
    getDataFromApi: () => void
    canDelete: boolean
}

const FaqResponseDisplay = (props: IFaqResponseDisplayProps) => {
    const {
        item,
        onEdit,
        onDelete,
        selectedFaqItem,
        getDataFromApi,
        canDelete = false,
    } = props

    return (
        <div className={styles.faqResponse__row}>
            <div className={styles.faqResponse__row__text}>{item.text}</div>

            <div className={styles.faqResponse__language}>
                <Earth className={styles.faqResponse__language__languageIcon} />
                <input
                    className={styles.faqResponse__language__languageInputView}
                    value={item.condition[0].value}
                    readOnly
                />
            </div>

            <button className={styles.faqResponse__borderBtn} onClick={onEdit}>
                <Pencil />
            </button>
            <FaqResponseYaml
                message={item}
                getDataFromApi={getDataFromApi}
                selectedFaqItem={selectedFaqItem}
            />
            {canDelete && (
                <button
                    className={styles.faqResponse__borderBtn}
                    onClick={onDelete}
                >
                    <Thrash />
                </button>
            )}
        </div>
    )
}

export default FaqResponsesSection
